export const OLLIE_ORDERING_LINE_STATUS = {
  MATCHED: 'matched',
  AMBIGUOUS: 'ambiguous',
  UNMATCHED: 'unmatched',
}

export const OLLIE_ORDERING_QUANTITY_INTENT = {
  SET: 'set',
  ADD: 'add',
  UNCLEAR: 'unclear',
}

export const OLLIE_ORDERING_PROPOSAL_QUESTION = {
  WHICH_ONE: 'which_one',
  ADD_CATALOGUE: 'add_catalogue',
  CANT_FIND: 'cant_find',
}

function toQuantity(value) {
  if (value == null || value === '') return null
  const amount =
    typeof value === 'number' ? value : Number(String(value).trim())
  if (!Number.isFinite(amount) || amount < 0) return null
  return amount
}

function formatQty(amount) {
  if (Number.isInteger(amount)) return String(amount)
  return String(amount)
}

/**
 * Basket-impact copy: 4 already in the basket + set 8 → "4 → 8".
 * New lines (nothing in the basket) show the resulting qty only.
 * Unclear set-vs-add does not invent a number.
 */
export function formatOllieOrderingQuantity(line) {
  const current = toQuantity(line?.currentQuantity)
  const resulting = toQuantity(line?.resultingQuantity)
  const requested = toQuantity(line?.quantity)
  const intentUnclear =
    line?.quantityIntent === OLLIE_ORDERING_QUANTITY_INTENT.UNCLEAR

  if (intentUnclear) {
    return current != null ? `${formatQty(current)} → ?` : '?'
  }

  if (resulting != null) {
    if (current != null && current > 0) {
      return `${formatQty(current)} → ${formatQty(resulting)}`
    }
    return formatQty(resulting)
  }

  if (requested != null) {
    return formatQty(requested)
  }

  return ''
}

function titledCandidates(rows) {
  if (!Array.isArray(rows)) return []
  return rows.filter(candidate => candidate && candidate.title)
}

export const OLLIE_ORDERING_FEATURED_CHOICES = 1

export function splitOllieOrderingChoices(candidates) {
  const rows = titledCandidates(candidates)
  if (rows.length <= OLLIE_ORDERING_FEATURED_CHOICES) {
    return { featured: rows, rest: [] }
  }
  return {
    featured: rows.slice(0, OLLIE_ORDERING_FEATURED_CHOICES),
    rest: rows.slice(OLLIE_ORDERING_FEATURED_CHOICES),
  }
}

export function ollieOrderingChoiceBadge(candidate, { featured } = {}) {
  if (candidate?.recommendedReason === 'order_history') {
    return 'Usually ordered'
  }
  if (featured || candidate?.recommended) {
    return 'Suggested'
  }
  return ''
}

export function ollieOrderingWhichOneCandidates(line) {
  if (line?.question !== OLLIE_ORDERING_PROPOSAL_QUESTION.WHICH_ONE) {
    return []
  }
  return titledCandidates(line?.candidates)
}

export function isOllieOrderingWhichOneLine(line) {
  return ollieOrderingWhichOneCandidates(line).length > 0
}

export function ollieOrderingCatalogueCandidates(line) {
  if (line?.question !== OLLIE_ORDERING_PROPOSAL_QUESTION.ADD_CATALOGUE) {
    return []
  }
  return titledCandidates(line?.catalogueCandidates)
}

export function isOllieOrderingAddCatalogueLine(line) {
  return ollieOrderingCatalogueCandidates(line).length > 0
}

export function isOllieOrderingAskLine(line) {
  return (
    isOllieOrderingWhichOneLine(line) || isOllieOrderingAddCatalogueLine(line)
  )
}

export function hasOllieOrderingOpenAsks(proposal) {
  return (Array.isArray(proposal?.lines) ? proposal.lines : []).some(
    isOllieOrderingAskLine,
  )
}

/**
 * A product the chef can see and expects in the basket, but Express would skip
 * because set-vs-add is still unresolved. It blocks Confirm rather than being
 * dropped silently at write time; the ± stepper answers it.
 */
export function isOllieOrderingQuantityAsk(line) {
  if (isOllieOrderingAskLine(line)) return false
  if (line?.question === OLLIE_ORDERING_PROPOSAL_QUESTION.CANT_FIND)
    return false
  if (!line?.productId) return false
  return line?.quantityIntent === OLLIE_ORDERING_QUANTITY_INTENT.UNCLEAR
}

export function hasOllieOrderingQuantityAsks(proposal) {
  return (Array.isArray(proposal?.lines) ? proposal.lines : []).some(
    isOllieOrderingQuantityAsk,
  )
}

export function canOllieOrderingEditQuantity(line) {
  if (isOllieOrderingAskLine(line)) return false
  if (line?.question === OLLIE_ORDERING_PROPOSAL_QUESTION.CANT_FIND)
    return false
  return Boolean(
    line?.title || line?.status === OLLIE_ORDERING_LINE_STATUS.MATCHED,
  )
}

/**
 * Same rule as Express confirm: a chef-accepted product, ready to write.
 */
export function canOllieOrderingConfirmLine(line) {
  if (isOllieOrderingAskLine(line)) return false
  if (line?.question) return false
  if (line?.status && line.status !== OLLIE_ORDERING_LINE_STATUS.MATCHED) {
    return false
  }
  if (!line?.productId) return false
  if (line?.quantityIntent === OLLIE_ORDERING_QUANTITY_INTENT.UNCLEAR) {
    return false
  }
  return true
}

/**
 * Confirm stays hidden while any shown product would be skipped, so the basket
 * always receives every line the chef is looking at.
 */
export function canOllieOrderingConfirm(proposal) {
  const lines = Array.isArray(proposal?.lines) ? proposal.lines : []
  if (!lines.length) return false
  if (hasOllieOrderingOpenAsks(proposal)) return false
  if (hasOllieOrderingQuantityAsks(proposal)) return false
  return lines.some(canOllieOrderingConfirmLine)
}

export const OLLIE_ORDERING_PICK_HINT =
  'Pick the items above, then you can add them to the basket.'

export const OLLIE_ORDERING_QUANTITY_HINT =
  'Set how many you need on the highlighted items, then you can add them to the basket.'

export function ollieOrderingBlockedHint(proposal) {
  if (hasOllieOrderingOpenAsks(proposal)) return OLLIE_ORDERING_PICK_HINT
  if (hasOllieOrderingQuantityAsks(proposal))
    return OLLIE_ORDERING_QUANTITY_HINT
  return ''
}

/**
 * How long the stepper waits after the last tap before saving. Long enough to
 * carry a run of taps into one correction, short enough that Confirm appears
 * while the chef is still looking at the line.
 */
export const OLLIE_ORDERING_QUANTITY_DEBOUNCE_MS = 400

export const OLLIE_ORDERING_PROPOSAL_HEADING = 'Proposed order'
export const OLLIE_ORDERING_CONFIRM_LABEL = 'Confirm and add to basket'
export const OLLIE_ORDERING_CONFIRMING_LABEL = 'Adding…'

export const OLLIE_ORDERING_WONT_ADD_BADGE = "Won't be added"

/**
 * A line the chef can see that confirm will step over: Ollie found nothing to
 * order for it. Asks are not skipped — they are waiting on an answer.
 */
export function isOllieOrderingSkippedLine(line) {
  if (!line) return false
  if (isOllieOrderingAskLine(line)) return false
  if (isOllieOrderingQuantityAsk(line)) return false
  return !line.productId
}

/**
 * Said above Confirm when the basket will not get everything on screen, so
 * "add it all" never quietly means "add most of it".
 */
export function ollieOrderingConfirmSummary(proposal) {
  const lines = Array.isArray(proposal?.lines) ? proposal.lines : []
  if (!lines.length) return ''
  const going = lines.filter(canOllieOrderingConfirmLine).length
  if (!going || going === lines.length) return ''
  const items = lines.length === 1 ? 'item' : 'items'
  return `${going} of ${lines.length} ${items} will be added`
}

/**
 * What Express actually wrote, and what it stepped over, told after the fact.
 */
export function ollieOrderingConfirmNotice({ written, skipped } = {}) {
  const added = Array.isArray(written) ? written.length : Number(written) || 0
  const names = (Array.isArray(skipped) ? skipped : [])
    .map(row => String(row?.title || row?.phrase || '').trim())
    .filter(Boolean)

  const addedText =
    added === 1
      ? '1 item added to your basket'
      : `${added} items added to your basket`
  if (!names.length) return addedText
  return `${addedText}. Not added: ${names.join(', ')}.`
}

/**
 * The Free-plan nudge Express already sends, said where the chef is typing
 * rather than only when the allowance has gone.
 */
export function ollieOrderingUsageWarning(usage) {
  const warning = usage?.warning
  const message = String(warning?.message || '').trim()
  if (!message) return ''

  const monthly = warning.type === 'monthly'
  const remaining = Number(
    monthly ? warning.monthlyRemaining : warning.dailyRemaining,
  )
  if (!Number.isFinite(remaining) || remaining < 0) return message

  const searches =
    remaining === 1 ? '1 Ollie search' : `${remaining} Ollie searches`
  const when = monthly ? 'this month' : 'today'
  const plan = String(usage?.plan || '').trim()
  return plan
    ? `${searches} left ${when} on the ${plan} plan.`
    : `${searches} left ${when}.`
}

export const OLLIE_ORDERING_EMPTY_HINT =
  'Tell me a product and how much — for example “2 bacon” or “3kg haddock”.'

export function ollieOrderingStepperQuantity(line) {
  const resulting = toQuantity(line?.resultingQuantity)
  if (resulting != null) return resulting
  const requested = toQuantity(line?.quantity)
  if (requested != null) return requested
  const current = toQuantity(line?.currentQuantity)
  if (current != null && current > 0) return current
  return 1
}

export function ollieOrderingLineTitle(line) {
  if (isOllieOrderingWhichOneLine(line)) {
    return line.phrase ? `Which ${line.phrase}?` : 'Which one?'
  }
  if (isOllieOrderingAddCatalogueLine(line)) {
    return String(line?.phrase || '').trim()
  }
  return String(line?.title || line?.phrase || '').trim()
}

export function ollieOrderingLineHint(line) {
  if (isOllieOrderingWhichOneLine(line)) {
    return ''
  }
  if (line?.question === OLLIE_ORDERING_PROPOSAL_QUESTION.ADD_CATALOGUE) {
    return 'Ollie found these — add?'
  }
  if (line?.quantityIntent === OLLIE_ORDERING_QUANTITY_INTENT.UNCLEAR) {
    return 'Is that a new total or extra?'
  }
  if (
    line?.question === OLLIE_ORDERING_PROPOSAL_QUESTION.CANT_FIND ||
    line?.message
  ) {
    return line.message || "I can't find that"
  }
  return ''
}
